import { motion } from 'framer-motion'
import { Construction, Clock, Sparkles } from 'lucide-react'

interface ComingSoonProps {
  title?: string
  description?: string
  estimatedDate?: string
  showProgress?: boolean
}

export function ComingSoon({
  title = 'Bientôt disponible',
  description = 'Cette fonctionnalité est en cours de développement.',
  estimatedDate,
  showProgress = true,
}: ComingSoonProps) {
  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md"
      >
        {/* Animated construction icon */}
        <motion.div
          className="relative inline-block mb-6"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 flex items-center justify-center">
            <Construction className="w-12 h-12 text-amber-600 dark:text-amber-400" />
          </div>

          {/* Sparkles decoration */}
          <motion.div
            className="absolute -top-2 -right-2"
            animate={{ rotate: 360 }}
            transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
          >
            <Sparkles className="w-6 h-6 text-amber-400" />
          </motion.div>
        </motion.div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
          {title}
        </h2>

        {/* Description */}
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          {description}
        </p>

        {/* Progress indicator */}
        {showProgress && (
          <div className="space-y-2 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Clock className="w-4 h-4" />
              <span>Développement en cours</span>
            </div>
            <div className="w-48 h-2 mx-auto bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: '60%' }}
                transition={{ duration: 1.5, delay: 0.5 }}
              />
            </div>
          </div>
        )}

        {/* Estimated date */}
        {estimatedDate && (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800 text-sm text-slate-600 dark:text-slate-400">
            <Clock className="w-4 h-4" />
            <span>Disponible: {estimatedDate}</span>
          </div>
        )}

        {/* Decorative dots */}
        <div className="flex justify-center gap-2 mt-8">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-amber-400"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.5, delay: i * 0.2, repeat: Infinity }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  )
}
